import React, { useState, useEffect, useRef } from "react";
import CustomModal from "./customModal";
import CategoryListIcon from "../CategoryListIcon/CategoryListIcon";
import GridSectionLoader from "../preLoad/gridSectionLoader";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import {
  SelectionModalHeaderActions,
  SelectionModalEmptyCTA,
  SelectionModalRefreshButton,
} from "./SelectionModalQuickLinks";
import "./parentCategoryModel.scss";

export default function CategoryModal({
  showParentCategoryModal,
  setShowParentCategoryModal,
  showButton,
  onSubmit,
  setSelectedParentCategory,
  selectedParentCategory,
  categories,
  isMultiSelect = false,
  modalTitle,
  modalSubTitle,
  fetchCategories,
  categoriesLoading = false,
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [tempSelectedCategories, setTempSelectedCategories] = useState(
    selectedParentCategory || (isMultiSelect ? [] : null)
  );
  const fetchCategoriesRef = useRef(fetchCategories);
  const hasFetchedRef = useRef(false);
  const prevModalStateRef = useRef(false);

  // Update ref when fetchCategories changes
  useEffect(() => {
    fetchCategoriesRef.current = fetchCategories;
  }, [fetchCategories]);

  useEffect(() => {
    // Only fetch when modal opens (transitions from false to true)
    const justOpened = showParentCategoryModal && !prevModalStateRef.current;
    prevModalStateRef.current = showParentCategoryModal;
    
    if (justOpened && typeof fetchCategoriesRef.current === "function") {
      hasFetchedRef.current = false;
      // Use setTimeout to ensure this runs after state updates
      setTimeout(() => {
        if (!hasFetchedRef.current && typeof fetchCategoriesRef.current === "function") {
          hasFetchedRef.current = true;
          fetchCategoriesRef.current({ showGlobalLoading: false });
        }
      }, 0);
    }
    
    // Reset the flag when modal closes
    if (!showParentCategoryModal) {
      hasFetchedRef.current = false;
    }
  }, [showParentCategoryModal]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(categories);
    } else {
      const filtered = filterCategories(
        categories,
        searchTerm.trim().toLowerCase()
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  useEffect(() => {
    setTempSelectedCategories(selectedParentCategory || (isMultiSelect ? [] : null));
  }, [selectedParentCategory, isMultiSelect]);

  const handleCategoryClick = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelectedCategories);
      setSelectedParentCategory(tempSelectedCategories);
    }
    setShowParentCategoryModal(false);
  };

  const handleRefreshList = async () => {
    if (typeof fetchCategoriesRef.current !== "function") return;
    setIsRefreshing(true);
    try {
      await fetchCategoriesRef.current({
        showGlobalLoading: false,
        skipParentAutoSelect: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const filterCategories = (categoriesArray, term) => {
    if (!term) return categoriesArray;
    return categoriesArray
      .map((category) => {
        const name = (category?.rootCat_Name || category?.RootCat_Name || "")
          .trim()
          .toLowerCase();
        const matchesSearch = name.includes(term);

        let filteredSubCategories = [];
        if (
          category.subCategoriesList &&
          category.subCategoriesList.length > 0
        ) {
          filteredSubCategories = filterCategories(
            category.subCategoriesList,
            term
          );
        }

        if (matchesSearch || filteredSubCategories.length > 0) {
          return {
            ...category,
            subCategoriesList: filteredSubCategories,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getRowCategoryId = (cat) =>
    cat?.rootCat_ID ??
    cat?.RootCat_ID ??
    cat?.categoryID ??
    cat?.CategoryID ??
    cat?.categoryId ??
    null;

  const flattenCategoryRows = (list, level = 0) => {
    const rows = [];
    (Array.isArray(list) ? list : []).forEach((category, index) => {
      if (!category) return;
      const categoryId = getRowCategoryId(category);
      rows.push({ category, level, index, categoryId });
      const children =
        category.subCategoriesList ||
        category.SubCategoriesList ||
        category.subCategories ||
        [];
      const hasSubcategories = Array.isArray(children) && children.length > 0;
      if (hasSubcategories && expandedCategories.has(categoryId)) {
        rows.push(...flattenCategoryRows(children, level + 1));
      }
    });
    return rows;
  };

  const handleCategorySelection = (category, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const categoryId = getRowCategoryId(category);
    if (categoryId == null) return;

    if (isMultiSelect) {
      setTempSelectedCategories((prev) => {
        const currentSelection = Array.isArray(prev) ? prev : [];
        const alreadySelected = currentSelection.some(
          (cat) => String(getRowCategoryId(cat)) === String(categoryId)
        );
        if (alreadySelected) {
          return currentSelection.filter(
            (cat) => String(getRowCategoryId(cat)) !== String(categoryId)
          );
        }
        return [...currentSelection, category];
      });
    } else {
      setTempSelectedCategories(category);
    }
  };

  const categoryRows = flattenCategoryRows(filteredCategories);

  return (
    <div>
      <CustomModal
        isOpen={showParentCategoryModal}
        title={modalTitle || "Parent Category"}
        subtitle={modalSubTitle || "Select a parent category"}
        onClose={() => setShowParentCategoryModal(false)}
        onSubmit={handleCategoryClick}
        showButton={showButton}
        showCancelButton={true}
        showSubmitButton={true}
        isCentered={true}
        submitButtonText="Done"
        modalSize="modal-lg"
        headerActions={<SelectionModalHeaderActions variant="category" />}
        zIndexBase={10050}
      >
        <div className="d-flex align-items-stretch gap-2 mb-3">
          <div className="input-group flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder="Search categories"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <span
                className="input-group-text"
                onClick={() => setSearchTerm("")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSearchTerm("");
                }}
              >
                <i className="fa fa-times"></i>
              </span>
            )}
          </div>
          {typeof fetchCategories === "function" && (
            <SelectionModalRefreshButton
              onClick={handleRefreshList}
              loading={isRefreshing}
            />
          )}
        </div>
        <GridSectionLoader loading={categoriesLoading} minHeight="220px">
          <div className="table-responsive parent-category-modal__list">
            <table
              className={`table mb-0 parent-category-modal__table${
                isDarkMode ? " parent-category-modal__table--dark" : ""
              }`}
            >
              <tbody>
                {categoryRows.length > 0 ? (
                  categoryRows.map(({ category, level, categoryId }, rowIndex) => {
                    const children =
                      category.subCategoriesList ||
                      category.SubCategoriesList ||
                      category.subCategories ||
                      [];
                    const hasSubcategories = Array.isArray(children) && children.length > 0;
                    const isExpanded = expandedCategories.has(categoryId);
                    const isSelected = isMultiSelect
                      ? (Array.isArray(tempSelectedCategories) ? tempSelectedCategories : []).some(
                          (cat) => String(getRowCategoryId(cat)) === String(categoryId)
                        )
                      : String(getRowCategoryId(tempSelectedCategories)) === String(categoryId);

                    return (
                      <tr
                        key={`cat-row-${categoryId ?? "x"}-${level}-${rowIndex}`}
                        className={`parent-category-modal__row${
                          isSelected ? " parent-category-modal__row--selected" : ""
                        }${isDarkMode ? " parent-category-modal__row--dark" : ""}`}
                        style={{
                          cursor: "pointer",
                          "--parent-cat-theme": themeColor,
                          "--parent-cat-selected-bg": `${themeColor}22`,
                        }}
                        onClick={(e) => handleCategorySelection(category, e)}
                      >
                        <td className="align-middle border-0">
                          <div className="d-flex align-items-center">
                            {Array.from({ length: level }).map((_, i) => (
                              <div key={i} style={{ width: "20px", flexShrink: 0 }} />
                            ))}

                            <div
                              className="me-2 parent-category-modal__chevron"
                              style={{
                                width: "20px",
                                flexShrink: 0,
                                cursor: hasSubcategories ? "pointer" : "default",
                                color: isDarkMode ? "#adb5bd" : "#6c757d",
                                visibility: hasSubcategories ? "visible" : "hidden",
                              }}
                              onClick={
                                hasSubcategories
                                  ? (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleExpand(categoryId);
                                    }
                                  : undefined
                              }
                              aria-hidden={!hasSubcategories}
                            >
                              <i
                                className={`fa ${
                                  isExpanded ? "fa-chevron-down" : "fa-chevron-right"
                                }`}
                              />
                            </div>

                            <CategoryListIcon
                              src={category.rootCat_Image || category.RootCat_Image}
                              alt={category.rootCat_Name || category.RootCat_Name}
                              className="me-2"
                              isDarkMode={isDarkMode}
                            />
                            <span
                              className={isSelected ? "fw-semibold" : ""}
                              style={{ color: isDarkMode ? "#f1f5f9" : "#212529" }}
                            >
                              {category.rootCat_Name || category.RootCat_Name}
                            </span>
                          </div>
                        </td>
                        <td className="align-middle text-end border-0" style={{ width: "50px" }}>
                          <span
                            className={`parent-category-modal__check${
                              isSelected ? " parent-category-modal__check--checked" : ""
                            }${isMultiSelect ? "" : " parent-category-modal__check--radio"}${
                              isDarkMode ? " parent-category-modal__check--dark" : ""
                            }`}
                            role={isMultiSelect ? "checkbox" : "radio"}
                            aria-checked={!!isSelected}
                            aria-label={`Select ${category.rootCat_Name || category.RootCat_Name || "category"}`}
                          >
                            {isSelected && isMultiSelect && (
                              <i className="fa fa-check" aria-hidden="true" />
                            )}
                            {isSelected && !isMultiSelect && (
                              <span className="parent-category-modal__check-dot" aria-hidden="true" />
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : !categoriesLoading && (!categories || categories.length === 0) ? (
                  <tr>
                    <td colSpan="2" className="p-0 border-0">
                      <SelectionModalEmptyCTA variant="category" />
                    </td>
                  </tr>
                ) : !categoriesLoading ? (
                  <tr>
                    <td
                      colSpan="2"
                      className="text-center py-3 border-0"
                      style={{ color: isDarkMode ? "#94a3b8" : "#6c757d" }}
                    >
                      No categories found
                      {searchTerm.length > 0
                        ? ` matching "${searchTerm}"`
                        : ""}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </GridSectionLoader>
      </CustomModal>
    </div>
  );
}